import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, ShoppingBag, Check, Gift, Users as Community, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

// Green theme tokens
const THEME = {
  primary: '#86EFAC',
  light: '#DCFCE7',
  lighter: '#F0FDF4',
  dark: '#166534',
  icon: '#22C55E',
  border: '#D1FAE5',
  surface: '#FAFDF7',
};

const EventsTab = () => {
  const [eventsData, setEventsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEventsData();
  }, []);

  const fetchEventsData = async () => {
    try {
      const response = await API.get('/shopping/by-event');
      setEventsData(response.data);
    } catch (error) {
      console.error('Failed to fetch events data:', error);
      toast.error('Failed to load events data');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      birthday: Gift,
      family: Community,
      community: Community,
      appointment: CalendarDays,
      reservation: Calendar,
      membership: Calendar
    };
    const Icon = icons[category] || Calendar;
    return Icon;
  };

  const getCategoryColor = (category) => {
    const colors = {
      birthday: { bg: '#FCE7F3', border: '#F9A8D4', dark: '#9D174D' },
      family: { bg: '#DBEAFE', border: '#93C5FD', dark: '#1E40AF' },
      community: { bg: '#EDE9FE', border: '#C4B5FD', dark: '#5B21B6' },
      appointment: { bg: '#DCFCE7', border: '#86EFAC', dark: '#166534' },
      reservation: { bg: '#FFEDD5', border: '#FDBA74', dark: '#9A3412' },
      membership: { bg: '#FEF3C7', border: '#FCD34D', dark: '#92400E' }
    };
    return colors[category] || { bg: THEME.lighter, border: THEME.primary, dark: THEME.dark };
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="mb-8">
        <h3 
          className="text-xl font-semibold"
          style={{ color: THEME.dark }}
        >
          Shopping by Event
        </h3>
        <p className="text-sm text-gray-500 mt-1">Items linked to Important Dates</p>
      </div>

      {eventsData.length === 0 ? (
        <div 
          className="text-center py-16 rounded-2xl border-2 border-dashed"
          style={{ 
            backgroundColor: THEME.lighter,
            borderColor: THEME.border,
          }}
        >
          <div 
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: THEME.light }}
          >
            <Calendar className="h-8 w-8" style={{ color: THEME.dark }} />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No event-linked items</h4>
          <p className="text-gray-500 max-w-sm mx-auto">
            Link shopping items to events to see them here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {eventsData.map((eventData) => {
            const categoryColors = getCategoryColor(eventData.event_category);
            const Icon = getCategoryIcon(eventData.event_category);
            
            return (
              <Card 
                key={eventData.event_id} 
                className="overflow-hidden hover:shadow-lg transition-all duration-200"
                style={{ 
                  borderColor: THEME.border,
                  borderWidth: '1px',
                  borderLeftWidth: '4px',
                  borderLeftColor: categoryColors.border,
                }}
              >
                <CardHeader className="pb-3 pt-5 px-5">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 text-gray-900">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: categoryColors.bg }}
                      >
                        <Icon className="h-4 w-4" style={{ color: categoryColors.dark }} />
                      </div>
                      <span className="truncate">{eventData.event_title}</span>
                    </CardTitle>
                    {eventData.event_date && (
                      <Badge 
                        variant="outline" 
                        className="text-xs font-medium flex-shrink-0 ml-2"
                        style={{ 
                          borderColor: categoryColors.border,
                          color: categoryColors.dark,
                        }}
                      >
                        {new Date(eventData.event_date + 'T00:00:00').toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric'
                        })}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="flex gap-2 mb-4">
                    <Badge 
                      variant="outline" 
                      className="text-xs font-medium"
                      style={{ 
                        borderColor: THEME.border,
                        color: THEME.dark,
                      }}
                    >
                      <ShoppingBag className="h-3 w-3 mr-1.5" />
                      {eventData.needed_count} needed
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className="text-xs font-medium"
                      style={{ 
                        borderColor: THEME.light,
                        color: THEME.dark,
                        backgroundColor: THEME.lighter,
                      }}
                    >
                      <Check className="h-3 w-3 mr-1.5" />
                      {eventData.purchased_count} done
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    {eventData.items.slice(0, 5).map((item) => (
                      <div 
                        key={item.id} 
                        className={`text-sm p-3 rounded-lg ${
                          item.status === 'purchased' 
                            ? 'line-through' 
                            : ''
                        }`}
                        style={{
                          backgroundColor: item.status === 'purchased' 
                            ? THEME.light 
                            : THEME.lighter,
                          color: item.status === 'purchased' 
                            ? THEME.dark 
                            : '#374151',
                        }}
                      >
                        <span className="font-medium">{item.name}</span>
                        {item.why_buying && (
                          <span className="text-xs block mt-1 opacity-70">
                            {item.why_buying}
                          </span>
                        )}
                      </div>
                    ))}
                    {eventData.items.length > 5 && (
                      <p className="text-xs text-gray-400 pl-3 pt-1">
                        +{eventData.items.length - 5} more items
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
};

export default EventsTab;
